import { useCallback, useEffect, useState } from "react"
import { CloseOutlined } from "@ant-design/icons"
import { Flex, Modal, Tabs, Typography } from "antd"
import { SERVICE_POLICY_URL } from "~/common/links"
import { ROUTER } from "~/common/router"
import { CrownIcon } from "~/components/Icons"
import { Text } from "~/components/Text"
import { useSessionStore } from "~/store/session"
import { useNavigate } from "react-router"
import { PaymentModal } from "./PaymentModal"
import {
  PROFESSIONAL_UPGRADE,
  type PurchaseSelection,
} from "./professionalPlan"
import styles from "./index.css"

import {
  freePointUnitPrice,
  professionalPointUnitPrice,
  type PurchaseHandler,
} from "./shared"
import { UpgradeContent } from "./UpgradeContent"
import {
  FreeRechargeContent,
  ProfessionalRechargeContent,
} from "./RechargeContent"
export { ContactPopover } from "./ContactPopover"

const { Title } = Typography

type RechargeUpgradeModalProps = {
  open: boolean
  onCancel: () => void
  defaultTab?: RechargeModalTabKey
  directUpgrade?: boolean
}

export type RechargeModalTabKey = "upgrade" | "recharge"

export const RechargeUpgradeModal = (props: RechargeUpgradeModalProps) =>
  props.open ? <RechargeUpgradeContent {...props} /> : null

const RechargeUpgradeContent = ({
  open,
  onCancel,
  defaultTab,
  directUpgrade = false,
}: RechargeUpgradeModalProps) => {
  const isProfessional = useSessionStore((state) => state.isProfessional)
  const refreshUser = useSessionStore((state) => state.refreshUser)
  const navigate = useNavigate()
  const [purchaseSelection, setPurchaseSelection] = useState<
    PurchaseSelection | undefined
  >(() => (directUpgrade && !isProfessional ? PROFESSIONAL_UPGRADE : undefined))

  const handlePaymentCancel = useCallback(() => {
    setPurchaseSelection(undefined)
    onCancel()
  }, [onCancel])

  const handlePaymentPaid = useCallback(async () => {
    await refreshUser().catch(() => undefined)
  }, [refreshUser])

  const handlePaymentGoHome = useCallback(() => {
    setPurchaseSelection(undefined)
    onCancel()
    navigate(ROUTER.HOME)
  }, [navigate, onCancel])

  if (purchaseSelection) {
    return (
      <PaymentModal
        intent={purchaseSelection.intent}
        open={open}
        originalAmount={purchaseSelection.originalAmount}
        policyUrl={SERVICE_POLICY_URL}
        successDescription={purchaseSelection.successDescription}
        successTitle={purchaseSelection.successTitle}
        onCancel={handlePaymentCancel}
        onComplete={handlePaymentCancel}
        onGoHome={handlePaymentGoHome}
        onPaid={handlePaymentPaid}
      />
    )
  }

  if (isProfessional) {
    return (
      <ProfessionalRechargeModal
        open={open}
        onCancel={onCancel}
        onPurchase={setPurchaseSelection}
      />
    )
  }

  return (
    <FreeRechargeUpgradeModal
      defaultTab={defaultTab}
      open={open}
      onCancel={onCancel}
      onPurchase={setPurchaseSelection}
    />
  )
}

type ModalBaseProps = {
  open: boolean
  onCancel: () => void
  onPurchase: PurchaseHandler
}

type FreeRechargeUpgradeModalProps = ModalBaseProps & {
  defaultTab?: RechargeModalTabKey
}

const FreeRechargeUpgradeModal = ({
  open,
  onCancel,
  onPurchase,
  defaultTab,
}: FreeRechargeUpgradeModalProps) => {
  const preferredTabKey = defaultTab || "upgrade"
  const [activeTabKey, setActiveTabKey] =
    useState<RechargeModalTabKey>(preferredTabKey)

  useEffect(() => {
    if (!open) return

    setActiveTabKey(preferredTabKey)
  }, [open, preferredTabKey])

  return (
    <Modal
      centered
      footer={null}
      open={open}
      classNames={{
        container: styles.freeModalContainer,
        header: styles.freeModalHeader,
        body: styles.freeModalBody,
      }}
      title={<FreeModalTitle />}
      width={680}
      onCancel={onCancel}
    >
      <Tabs
        activeKey={activeTabKey}
        className={styles.freeTabs}
        classNames={{
          header: styles.tabsHeader,
          content: styles.tabsContent,
          item: styles.tabItem,
        }}
        items={[
          {
            key: "upgrade",
            label: "升级专业版",
            className: styles.upgradePane,
            children: <UpgradeContent onPurchase={onPurchase} />,
          },
          {
            key: "recharge",
            label: "积点充值",
            className: styles.rechargePane,
            children: (
              <FreeRechargeContent
                unitPrice={freePointUnitPrice}
                onViewProfessional={() => setActiveTabKey("upgrade")}
                onPurchase={onPurchase}
              />
            ),
          },
        ]}
        onChange={(key) => setActiveTabKey(key as RechargeModalTabKey)}
      />
    </Modal>
  )
}

const ProfessionalRechargeModal = ({
  open,
  onCancel,
  onPurchase,
}: ModalBaseProps) => {
  return (
    <Modal
      centered
      closeIcon={<CloseOutlined />}
      footer={null}
      open={open}
      title={<ProfessionalModalTitle />}
      width={680}
      onCancel={onCancel}
    >
      <ProfessionalRechargeContent
        unitPrice={professionalPointUnitPrice}
        onPurchase={onPurchase}
      />
    </Modal>
  )
}

const FreeModalTitle = () => {
  return (
    <Flex
      align="center"
      gap={12}
    >
      <CrownIcon className={styles.headerIcon} />
      <Text
        size="l"
        strong
      >
        升级会员或充值积点，畅享AI创作
      </Text>
    </Flex>
  )
}

const ProfessionalModalTitle = () => {
  return (
    <Flex
      vertical
      gap={8}
    >
      <Title level={4}>充值积点，畅享AI创作</Title>
      <Text type="secondary">专业版专享充值优惠，积点永久有效</Text>
    </Flex>
  )
}
